import {Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AppPaginationComponent} from "@shared/components/tables/app-pagination/app-pagination.component";
import {AppPaginationBaseAbstract} from "@shared/commons/pagination-base-abstract.utils";
import {User, UserProfile, UsersApiService} from "@core/services/api/users-api.service";
import {AlertService} from "@core/services/alert.service";
import {AppTableComponent} from "@shared/components/tables/app-table/app-table.component";
import {AppTableBodyTemplateDirective, AppTableHeaderTemplateDirective} from "@shared/components/tables/app-table/directives/app-table.directives";
import {AppTableHeaderColumnComponent} from "@shared/components/tables/app-table-header-column/app-table-header-column.component";
import {TranslateModule} from "@ngx-translate/core";
import {AppInputComponent} from "@shared/components/forms/app-input/app-input.component";
import {FormsModule} from "@angular/forms";
import {AppEmptyAlertMessageComponent} from "@shared/components/commons/app-empty-alert-message/app-empty-alert-message.component";
import {UserRegisterDialogComponent} from "@features/users/components/user-register-dialog/user-register-dialog.component";
import {UserChangePasswordDialogComponent} from "@features/users/components/user-change-password-dialog/user-change-password-dialog.component";
import {AppLoadingOverlayComponent} from "@shared/components/commons/app-loading-overlay/app-loading-overlay.component";

@Component({
    selector: 'app-users',
    standalone: true,
    imports: [
        CommonModule,
        AppPaginationComponent,
        AppTableComponent,
        AppTableHeaderTemplateDirective,
        AppTableHeaderColumnComponent,
        TranslateModule,
        AppInputComponent,
        FormsModule,
        AppTableBodyTemplateDirective,
        AppEmptyAlertMessageComponent,
        AppLoadingOverlayComponent
    ],
    templateUrl: './users-list.component.html',
    styleUrl: './users-list.component.scss'
})
export class UsersListComponent extends AppPaginationBaseAbstract implements OnInit {
    protected items: any[] = [];

    private modalService = inject(NgbModal);
    private alertService = inject(AlertService);
    private usersApiService = inject(UsersApiService);

    constructor() {
        super();
    }

    onInit(): void {
        this.addCustomSort([{field: 'name', direction: 'asc'}]);
    }

    findAll(params: any): void {
        this.usersApiService.findAll(params)
            .subscribe({
                next: (response) => {
                    const data = response.data;
                    this.items = data.result || [];
                    this.pagination.itemsAmount = data.totalResults;
                    this.setLoading(false);
                },
                error: () => this.alertService.operationFailure()
            })
    }

    getRoleBadgeClass(profile: UserProfile): string {
        return profile === UserProfile.ADMIN
            ? 'badge bg-dark text-white'
            : 'badge bg-light text-dark border';
    }

    getRoleLabel(profile: UserProfile): string {
        return profile === UserProfile.ADMIN ? 'Admin' : 'Profissional';
    }

    getUserInitials(name: string): string {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .substring(0, 2)
            .toUpperCase();
    }

    getAvatarColor(user: User): string {
        // Tons de cinza neutros ao invés de cores vibrantes
        const grays = ['#6c757d', '#495057', '#343a40', '#212529'];
        return grays[user.id! % grays.length];
    }

    openCreateModal(): void {
        const modalRef = this.modalService.open(UserRegisterDialogComponent, {
            centered: true,
            size: 'lg'
        });

        modalRef.componentInstance.isEditMode = false;

        modalRef.result.then(
            (user) => {
                if (user) {
                    this.updateSearch();
                }
            },
            () => {
                // Modal fechado sem ação
            }
        );
    }

    openEditModal(user: User): void {
        const modalRef = this.modalService.open(UserRegisterDialogComponent, {
            centered: true,
            size: 'lg'
        });

        modalRef.componentInstance.isEditMode = true;
        modalRef.componentInstance.user = user;

        modalRef.result.then(
            (updatedUser) => {
                if (updatedUser) {
                    this.updateSearch();
                }
            },
            () => {
                // Modal fechado sem ação
            }
        );
    }

    openChangePasswordModal(user: User): void {
        const modalRef = this.modalService.open(UserChangePasswordDialogComponent, {
            centered: true,
            size: 'md'
        });

        modalRef.componentInstance.userId = signal(user.id)
        modalRef.componentInstance.userFirstName = signal(user.firstName);
        modalRef.componentInstance.userLastName = signal(user.lastName);

        modalRef.result.then(
            (success) => {
                if (success) {
                    // Senha alterada com sucesso
                }
            },
            () => {
                // Modal fechado sem ação
            }
        );
    }

    deleteUser(user: User): void {
        if (!confirm(`Tem certeza que deseja excluir o usuário ${user.firstName} ${user.lastName}?`)) {
            return;
        }

        this.usersApiService.delete(user.id!).subscribe({
            next: () => {
                this.alertService.successfulOperation();
                this.updateSearch();
            },
            error: () => this.alertService.operationFailure()
        });
    }
}
